import { useState, useRef, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import useRetryFetch from "../hooks/useRetryFetch";
import useAuth from "../hooks/useAuth";
import { tryGetUserProfile } from "../api/auth";
import LoggerContext from "../context/LoggerProvider";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { directionService } from "../services/directionService";
import { Link } from "react-router-dom";
import "./Login.css";

import signInGoogle from "../images/SignInGoogle.png";

import { Spinner } from "./ui/spinner";

import { config } from "../Environment";
import { WSContext } from "../App";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Eye, EyeOff, AlertCircle, BookOpen, Users, TrendingUp, ArrowRight } from "lucide-react";

import PresListContext from "../context/PresListProvider";

function Login() {
  const { setAuth, setUser } = useAuth();
  const { Logger } = useContext(LoggerContext);
  const retryFetch = useRetryFetch();

  const emailRef = useRef();
  const { t } = useTranslation();
  const { socket } = useContext(WSContext);
  const { setContextPrescriptions } = useContext(PresListContext);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const fromPathname = location.state?.from?.pathname || "/home";
  const fromSearch = location.state?.from?.search || "";
  const fromHash = location.state?.from?.hash || "";
  const acceptId = location.state?.acceptId;

  const [authMethodsReady, setAuthMethodsReady] = useState(false);
  const [authMethods, setAuthMethods] = useState([]);
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [direction, setDirection] = useState(document.body.dir);

  async function getAuthMethods() {
    const response = await fetch(config.API_BASE + "/api/auth/methods");
    if (response.ok) {
      const responseJSON = await response.json();
      if (Array.isArray(responseJSON.list)) {
        return responseJSON.list;
      }
    }
    return [];
  }

  async function authenticateAndRedirect({ email, accessToken }) {
    Logger.info("Login.jsx authenticateAndRedirect");
    setAuth({ email, accessToken, impersonatedBy: undefined });

    const userProfile = await tryGetUserProfile(retryFetch);
    if (userProfile) {
      userProfile.roles ??= [];
      setUser(userProfile);
    } else {
      setErrMsg(t("msg_error_failed_loading_user_roles"));
    }

    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(
        JSON.stringify({
          command: "login",
          payload: { email },
        })
      );
    }

    setContextPrescriptions(null);

    const returnUrl = searchParams.get('return') || location.state?.returnUrl;
    const fromWizard = searchParams.get('from_wizard') === 'true' || location.state?.fromWizard;

    // Honor an explicit return URL after login (e.g. /join-mentor/:token, /join-collection).
    // Only allow in-app (relative) paths to avoid open-redirect to external sites.
    if (returnUrl) {
      const decodedReturn = decodeURIComponent(returnUrl);
      if (decodedReturn.startsWith('/')) {
        if (fromWizard) {
          Logger.info('[Wizard Integration] Redirecting back to wizard after authentication', { returnUrl });
        }
        navigate(decodedReturn, { replace: true });
        return;
      }
    }

    const engagementTransfer = location.state?.engagementTransfer;
    if (engagementTransfer?.processId) {
      Logger.info(
        `Attempting to transfer anonymous process ${engagementTransfer.processId} to user ${email}`
      );
      try {
        const response = await fetch(
          `${config.API_BASE}/api/processes/setuser/${engagementTransfer.processId}/${email}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            credentials: "include",
          }
        );

        if (response.ok) {
          Logger.info(
            `Successfully transferred process ${engagementTransfer.processId} to user ${email}`
          );
          navigate(`/activities/run?wid=${engagementTransfer.prescriptionId}&pid=${engagementTransfer.processId}&t=${Date.now()}`, {
            replace: true,
          });
          return;
        } else {
          const errorData = await response.json();
          Logger.error(`Failed to transfer process: ${JSON.stringify(errorData)}`);
        }
      } catch (error) {
        Logger.error(`Error transferring anonymous process: ${error}`);
      }
    }

    if (acceptId) {
      navigate(`/accept/${acceptId}?t=${Date.now()}`, { replace: true });
    } else if (sessionStorage.getItem("draftprescription")) {
      navigate(`/design?t=${Date.now()}`);
    } else {
      if (["/register", "/login"].includes(fromPathname)) {
        navigate(`/home?t=${Date.now()}`, { replace: true });
      } else {
        const prefix = fromSearch ? "&" : "?";
        const timestamp = `${prefix}t=${Date.now()}`;
        navigate(`${fromPathname}${fromSearch}${timestamp}${fromHash}`, {
          replace: true,
          state: location.state?.pendingAction ? { pendingAction: location.state.pendingAction } : undefined,
        });
      }
    }
  }

  useEffect(() => {
    if (location.state?.email && location.state?.accessToken) {
      Logger.info("Login.jsx got email and access token");
      setEmail(location.state?.email);
      setTimeout(() => {
        authenticateAndRedirect({
          email: location.state?.email,
          accessToken: location.state?.accessToken,
        });
      }, 10);
    }
  }, []);

  useEffect(() => {
    (async function displayAuthMethods() {
      const result = await getAuthMethods();
      setAuthMethods(result);
      setAuthMethodsReady(true);
    })();
  }, []);

  useEffect(() => {
    if (authMethodsReady && authMethods.includes("password")) {
      emailRef.current.focus();
    }
  }, [authMethods, authMethodsReady]);

  useEffect(() => {
    let directionUnsubscribe = directionService
      .getDirectionChange()
      .subscribe(() => {
        setDirection(document.body.dir);
      });

    return () => {
      directionUnsubscribe.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setErrMsg("");
  }, [email, pwd]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !pwd) {
      emailRef.current.focus();
      return setErrMsg(t("msg_error_email_and_password_required"));
    }

    setIsSubmitting(true);

    let response = await fetch(config.API_BASE + "/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password: pwd }),
      credentials: "include",
    });

    if (!response.ok) {
      setIsSubmitting(false);
      setErrMsg(t("msg_error_email_or_password_not_correct"));
      emailRef.current.focus();
      return;
    }

    setEmail("");
    setPwd("");
    let data = await response.json();
    let accessToken = data?.accessToken;

    await authenticateAndRedirect({ email, accessToken });
    setIsSubmitting(false);
  };

  const changeEmail = (e) => setEmail(e.target.value);
  const changePassword = (e) => setPwd(e.target.value);

  function renderLoadingAuthMethods() {
    if (authMethodsReady) return null;
    return (
      <div className="flex justify-center py-4">
        <Spinner size="md" />
      </div>
    );
  }

  function renderLoginDisabled() {
    if (authMethodsReady && authMethods.length === 0) {
      return (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{t("login_temporarily_disabled") || "Login is temporarily disabled. Please try again later."}</p>
        </div>
      );
    }
    return null;
  }

  function renderLoginWithPassword() {
    if (authMethodsReady && authMethods.includes("password")) {
      return (
        <form onSubmit={handleSubmit} noValidate>
          <div className="grid gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="email">{t("email_address")}</Label>
              <Input
                id="email"
                type="email"
                placeholder="m@example.com"
                onChange={changeEmail}
                ref={emailRef}
                value={email}
                autoComplete="email"
                required
                className="h-11"
              />
            </div>
            <div className="grid gap-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("password")}</Label>
                <Link
                  to="/forgotpassword"
                  className="text-xs text-primary hover:text-primary/80 underline-offset-4 hover:underline transition-colors"
                >
                  {t("forgot_your_password")}
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  onChange={changePassword}
                  value={pwd}
                  autoComplete="current-password"
                  required
                  className="h-11 pe-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 end-0 flex items-center pe-3 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? t("hide_password") || "Hide password" : t("show_password") || "Show password"}
                  style={{ touchAction: "manipulation" }}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {errMsg && (
              <div
                role="alert"
                aria-live="polite"
                className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{errMsg}</span>
              </div>
            )}

            <Button
              type="submit"
              className="h-11 w-full text-base font-medium"
              disabled={isSubmitting}
              style={{ touchAction: "manipulation" }}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Spinner size="sm" />
                  {t("logging_in") || "Signing in…"}
                </span>
              ) : (
                t("login")
              )}
            </Button>
          </div>
        </form>
      );
    }
    return null;
  }

  function renderLoginWithGoogle() {
    if (authMethodsReady && authMethods.includes("google")) {
      return (
        <button
          type="button"
          onClick={() => {
            if (location.state) {
              sessionStorage.setItem("locationstate", JSON.stringify(location.state));
            }
            window.open(`${config.API_BASE}/auth/google/redirect`, "_self");
          }}
          className="flex h-11 w-full items-center justify-center gap-3 rounded-md border border-input bg-background px-4 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          style={{ touchAction: "manipulation" }}
        >
          <img src={signInGoogle} alt="" className="h-5 w-5" />
          {t("login_with_google")}
        </button>
      );
    }
    return null;
  }

  const hasPasswordMethod = authMethodsReady && authMethods.includes("password");
  const hasGoogleMethod = authMethodsReady && authMethods.includes("google");
  const showDivider = hasPasswordMethod && hasGoogleMethod;

  return (
    <div className="min-h-dvh flex">
      {/* Left branding panel — desktop only */}
      <div
        className="hidden lg:flex lg:w-[480px] xl:w-[560px] shrink-0 flex-col justify-between p-12 text-white"
        style={{ background: "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.75) 100%)" }}
        aria-hidden="true"
      >
        {/* Friendly illustration — waving character */}
        <div className="flex justify-center">
          <svg viewBox="0 0 280 210" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-[280px]" aria-hidden="true">
            {/* Pulsing glow rings */}
            <circle cx="140" cy="108" r="82" fill="white" fillOpacity="0.04">
              <animate attributeName="r" values="82;90;82" dur="4s" repeatCount="indefinite"/>
              <animate attributeName="fill-opacity" values="0.04;0.08;0.04" dur="4s" repeatCount="indefinite"/>
            </circle>
            <circle cx="140" cy="108" r="58" fill="white" fillOpacity="0.04">
              <animate attributeName="r" values="58;64;58" dur="4s" begin="0.8s" repeatCount="indefinite"/>
            </circle>
            {/* Head */}
            <circle cx="140" cy="78" r="36" fill="white" fillOpacity="0.9"/>
            {/* Head glow ring */}
            <circle cx="140" cy="78" r="43" fill="none" stroke="white" strokeOpacity="0.18" strokeWidth="2">
              <animate attributeName="r" values="43;51;43" dur="3.5s" repeatCount="indefinite"/>
              <animate attributeName="stroke-opacity" values="0.18;0.05;0.18" dur="3.5s" repeatCount="indefinite"/>
            </circle>
            {/* Eyes */}
            <circle cx="129" cy="72" r="4" fill="hsl(var(--primary))"/>
            <circle cx="151" cy="72" r="4" fill="hsl(var(--primary))"/>
            {/* Smile */}
            <path d="M 128 87 Q 140 99 152 87" stroke="hsl(var(--primary))" strokeWidth="3" strokeLinecap="round" fill="none"/>
            {/* Body arch */}
            <path d="M 112 150 Q 140 122 168 150" stroke="white" strokeOpacity="0.5" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
            {/* Left arm (static) */}
            <path d="M 118 132 Q 96 122 78 134" stroke="white" strokeOpacity="0.5" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
            {/* Left hand */}
            <circle cx="75" cy="136" r="5" fill="white" fillOpacity="0.6"/>
            {/* Right arm (waving) */}
            <path d="M 162 132 Q 184 122 202 132" stroke="white" strokeOpacity="0.7" strokeWidth="2.5" strokeLinecap="round" fill="none">
              <animate attributeName="d" values="M 162 132 Q 184 122 202 132;M 162 132 Q 186 106 206 116;M 162 132 Q 184 122 202 132" dur="1.8s" repeatCount="indefinite" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.45 0 0.55 1;0.45 0 0.55 1"/>
            </path>
            {/* Right hand (waving) */}
            <circle r="5.5" fill="white" fillOpacity="0.75">
              <animate attributeName="cx" values="202;206;202" dur="1.8s" repeatCount="indefinite" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.45 0 0.55 1;0.45 0 0.55 1"/>
              <animate attributeName="cy" values="132;116;132" dur="1.8s" repeatCount="indefinite" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.45 0 0.55 1;0.45 0 0.55 1"/>
            </circle>
            {/* Legs */}
            <path d="M 130 150 L 125 185" stroke="white" strokeOpacity="0.4" strokeWidth="2.5" strokeLinecap="round"/>
            <path d="M 150 150 L 155 185" stroke="white" strokeOpacity="0.4" strokeWidth="2.5" strokeLinecap="round"/>
            {/* Ground shadow */}
            <ellipse cx="140" cy="193" rx="52" ry="6" fill="white" fillOpacity="0.07"/>
            {/* Sparkle top-left — floating */}
            <g>
              <path d="M 58 50 l2 5 l2 -5 l5 -2 l-5 -2 l-2 -5 l-2 5 l-5 2 z" fill="white" fillOpacity="0.65">
                <animateTransform attributeName="transform" type="translate" values="0,0;0,-7;0,0" dur="3s" repeatCount="indefinite" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.45 0 0.55 1;0.45 0 0.55 1"/>
                <animate attributeName="fill-opacity" values="0.65;1;0.65" dur="3s" repeatCount="indefinite"/>
              </path>
            </g>
            {/* Sparkle top-right — floating */}
            <g>
              <path d="M 214 45 l1.5 3.5 l1.5 -3.5 l3.5 -1.5 l-3.5 -1.5 l-1.5 -3.5 l-1.5 3.5 l-3.5 1.5 z" fill="white" fillOpacity="0.5">
                <animateTransform attributeName="transform" type="translate" values="0,0;0,-6;0,0" dur="2.8s" begin="0.6s" repeatCount="indefinite" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.45 0 0.55 1;0.45 0 0.55 1"/>
                <animate attributeName="fill-opacity" values="0.5;0.85;0.5" dur="2.8s" begin="0.6s" repeatCount="indefinite"/>
              </path>
            </g>
            {/* Sparkle above head — floating */}
            <g>
              <path d="M 163 24 l1.5 3.5 l1.5 -3.5 l3.5 -1.5 l-3.5 -1.5 l-1.5 -3.5 l-1.5 3.5 l-3.5 1.5 z" fill="white" fillOpacity="0.55">
                <animateTransform attributeName="transform" type="translate" values="0,0;0,-5;0,0" dur="3.5s" begin="1.2s" repeatCount="indefinite" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.45 0 0.55 1;0.45 0 0.55 1"/>
                <animate attributeName="fill-opacity" values="0.55;0.9;0.55" dur="3.5s" begin="1.2s" repeatCount="indefinite"/>
              </path>
            </g>
            {/* Floating ambient dots */}
            <circle cx="95" cy="44" r="3" fill="white" fillOpacity="0.4">
              <animate attributeName="cy" values="44;38;44" dur="3.5s" repeatCount="indefinite"/>
            </circle>
            <circle cx="192" cy="68" r="2.5" fill="white" fillOpacity="0.35">
              <animate attributeName="cy" values="68;63;68" dur="4s" begin="1s" repeatCount="indefinite"/>
            </circle>
            <circle cx="42" cy="92" r="2.5" fill="white" fillOpacity="0.3">
              <animate attributeName="cy" values="92;86;92" dur="3.2s" begin="0.8s" repeatCount="indefinite"/>
            </circle>
          </svg>
        </div>

        <div className="space-y-8">
          <div className="space-y-3">
            <h2 className="text-3xl font-bold leading-tight">
              {t("auth_brand_headline") || "Unlock your potential"}
            </h2>
            <p className="text-white/75 text-lg leading-relaxed">
              {t("auth_brand_subheadline") || "Guided activities and expert coaching to help you grow."}
            </p>
          </div>

          <ul className="space-y-4">
            <li className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20">
                <BookOpen className="h-4 w-4" />
              </span>
              <span className="text-white/90 text-sm">
                {t("auth_feature_activities") || "Guided activities tailored to your goals"}
              </span>
            </li>
            <li className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20">
                <Users className="h-4 w-4" />
              </span>
              <span className="text-white/90 text-sm">
                {t("auth_feature_experts") || "Connect with coaches and mentors"}
              </span>
            </li>
            <li className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20">
                <TrendingUp className="h-4 w-4" />
              </span>
              <span className="text-white/90 text-sm">
                {t("auth_feature_progress") || "Track your growth and celebrate progress"}
              </span>
            </li>
          </ul>
        </div>

        <p className="text-white/40 text-xs">
          © {new Date().getFullYear()} ClaryNext
        </p>
      </div>

      {/* Right form panel */}
      <div className="flex flex-1 items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-[400px] space-y-6">

          <div className="space-y-1.5 text-center">
            <h1 className="text-2xl font-bold tracking-tight">
              {t("welcome_back") || "Welcome back"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t("login_subtitle") || "Sign in to continue to your account"}
            </p>
          </div>

          {renderLoadingAuthMethods()}
          {renderLoginDisabled()}
          {renderLoginWithPassword()}

          {showDivider && (
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  {t("or")}
                </span>
              </div>
            </div>
          )}

          {renderLoginWithGoogle()}

          {/* Sign up CTA */}
          <div className="pt-2 text-center text-sm text-muted-foreground">
            {t("dont_have_an_account") || "Don't have an account?"}{" "}
            <Link
              to="/register"
              state={{
                from: location.state?.from,
                engagementTransfer: location.state?.engagementTransfer,
                pendingAction: location.state?.pendingAction,
                acceptId: location.state?.acceptId,
              }}
              className="font-medium text-primary underline-offset-4 hover:underline transition-colors inline-flex items-center gap-0.5"
            >
              {t("sign_up")}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
